import { v4 as uuid } from 'uuid'
import React, { JSX, memo, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { ChartContext } from '../chart.context'
import { SeriesGroup, SeriesOutput } from '../chart.typings'
import { SeriesGroupCategory } from '../../../pages/home'
import './seriesMetadataDisplay.css'
import { useDispatch, useSelector } from 'react-redux'
import { selectChartConfigSettings, selectChartPanes, selectChartPanesConfig, selectChartSeries, selectChartSeriesGroups, selectChartTradingSettings, selectIndicator, selectIndicatorScript } from '../../../selectors/chart'
import { Avatar, Box, ClickAwayListener, Collapse, IconButton, Stack, SvgIcon, Typography, useTheme } from '@mui/material'
import Popover from '@mui/material/Popover'
import SeriesGroupConfigurationPopup from './seriesGroupConfigPopup/seriesGroupConfigurationPopup'
import RemoveIndicatorDependentsDialog from './removeIndicatorDependentsDialog'
import SeriesMetadataLiveValues from './SeriesMetadataLiveValues'
import SeriesMetadataBidAskControls from './SeriesMetadataBidAskControls'
import SeriesMetadataPrimaryToolbar from './SeriesMetadataPrimaryToolbar'
import SeriesGroupMenu from './seriesGroupMenu/seriesGroupMenu'
import { ExtraSmallButton, NonInteractiveTooltip, UltraSmallButton } from '../../custom'
import SettingsOutlinedIcon from '../../../assets/icons/SettingOutlined'
import { LOGOS_BASE_URL } from '../../../assets/constants'
import {
    buildPatternDefaultInputValues,
    buildPatternDefaultOutputValues,
    ensureCandlestickPatternConfigurationFields,
    getCandlestickPatternBias,
    getPatternConfigurationFields,
    isCandlestickPatternIndicator,
    isChartPatternIndicator,
    isPatternIndicator,
    isPatternLabelColorInput,
    PATTERN_LABEL_COLOR_INPUT_CODE,
} from '../../../util/patternIndicators'
import { buildCandlestickLabelColorStylePatch } from '../../../assets/candlestickPatternConfig'
import { buildPatternDrawingSettingsUpdate, getPatternDrawingSettingsMap } from '../drawingTools/chartPatternOverlayTool/chartPatternOverlay.constants'
import { getColorForSymbol, mapIndicatorInputTypeToDrawingSettingType, toLocaleStringIn } from '../../../util/general'
import ChartDrawingSettingsModal from '../chartDrawingSettings/chartDrawingSettingsModal'
import { openConnectAccountDialog, setQuickOrderData, updateOrderHistory } from '../../../reducer/orders.reducer'
import { ChartEvents } from '../chart.events'
import { DrawingSettingGroup } from '../drawingTools/drawing.interface'
import { SettingType } from '../drawingTools/drawing.enums'
import { Security, SecurityCategory_CategoryCode } from '../../../proto/generated/security'
import store from '../../../store'
import { Broker_OrderType, Broker_Variety, Order, OrderStatus, OrderType, Product, TransactionType, Validity } from '../../../proto/generated/order'
import { setSecuritySearchOpen } from '../../../reducer/home.reducer'
import { getSecurityAsync, getSecurityKey, parseDetailsFromFutureSecurityCode, parseDetailsFromOptionSecurityCode } from '../../../util/security'
import Hide from '../../../assets/icons/Hide'
import Show from '../../../assets/icons/Show'
import { IndicatorInput, IndicatorInput_InputType } from '../../../proto/generated/indicator'
import { COMPARABLE_SECURITY_SOURCE_INPUT_CODE, getIndicatorDrawingSettings } from '../../../util/chart'
import { LineStyle } from 'lightweight-charts'
import { ChartLineStyle } from '../../colorSelector/lineStyle.types'

const OI_PROFILE_INPUT_SETTING_KEYS = new Set(['dataRenderType', 'cumulative'])

const OI_PROFILE_DATA_RENDER_TYPE_LABELS: Record<string, string> = {
    openInterest: 'Open Interest',
    openInterestChange: 'OI Change'
}

const OI_PROFILE_INPUT_FIELDS: IndicatorInput[] = [
    {
        title: 'Type of Data',
        code: 'dataRenderType',
        type: IndicatorInput_InputType.STRING,
        defval: 'openInterestChange',
        minval: '',
        maxval: '',
        options: ['openInterest', 'openInterestChange'],
        group: '',
        tooltip: '',
        display: true
    },
    {
        title: 'Cumulative',
        code: 'cumulative',
        type: IndicatorInput_InputType.BOOLEAN,
        defval: 'true',
        minval: '',
        maxval: '',
        options: [],
        group: '',
        tooltip: '',
        display: true
    }
]

const lineStyleEnumToChartLineStyle = (style: LineStyle | undefined): ChartLineStyle => {
    switch (style) {
        case LineStyle.Dotted:
            return 'dotted'
        case LineStyle.Dashed:
            return 'dashed'
        case LineStyle.LargeDashed:
            return 'large_dashed'
        case LineStyle.SparseDotted:
            return 'sparse_dotted'
        default:
            return 'solid'
    }
}
import { selectPrimarySecurity } from '../../../selectors/home'
import BinIcon from '../../../assets/icons/BinIcon'
import ErrorIcon from '@mui/icons-material/Error'
import ExpandLess from '@mui/icons-material/ExpandLess'
import ExpandMore from '@mui/icons-material/ExpandMore'
import { convertExpressionToDisplayExpression, getEmbeddedStrategyExpressionLegDisplayStrings, normalizeExpressionKeyFromUrl } from '../../../util/expression'
import { setTradeOptions, TradeOptions } from '../../../reducer/scalper.reducer'
import NumberInputField from '../../common/numberInputField/numberInputField'
import { selectPrimaryTradingAccount, selectTradingAccounts } from '../../../selectors/user'
import { PlaceOrModifyOrdersRequest, TradingAccountStatus } from '../../../proto/generated/trading-bridge'
import { SurveillanceWarningDialog } from '../../quickTradeWindow/RiskDisclaimationDialog'
import { placeOrModifyOrder } from '../../../services/apis/orders'
import { addOrderAlertMessage } from '../../../util/orders'
import { selectIsEmbedded } from '../../../selectors/device.ts'
import { isEmbeddedStrategyChartContext } from '../../../util/embeddedChartContext'
import { callNative } from '../../../util/nativeBridge'
import { isDatePassed } from '../../../util/tradepanel.ts'
import AlarmIcon from '../../../assets/icons/AlarmIcon'
import { selectUserAlerts } from '../../../selectors/alerts'
import { AlertExpression_Type, UserAlert_Status, UserAlert_Type } from '../../../proto/generated/nexus'
import type { AlertIndicator } from '../../../proto/generated/nexus'
import { getPrimarySecurityAlertCondition, getUserAlertUnderlyingSecurity, isUserAlertVolumeLevelThreshold, flattenAlertIndicatorParamsForChart, buildIndicatorSeriesGroupLookup } from '../../../util/alerts'
import { getIndicatorScript } from '../../../services/apis/misc'
import { parseIndicatorInputsFromScript } from '../../../util/indicatorScriptInputs'
import { selectIndicators } from '../../../selectors/chart'
import {
    decodeIndicatorOutputSource,
    getIndicatorInputSelectOptions,
    getIndicatorPriceSourceLabel,
    IndicatorOutputSourceOption,
    IndicatorSourceDependentDisplay,
    isIndicatorOutputSource,
    isIndicatorPriceSourceInput,
    isIndicatorSelectInput,
    sanitizeId
} from '../../../util/indicators'
import { getEffectiveChartSnapshotSeriesGroup, getChartSnapshotSeriesGroup } from '../chartSnapshotStore'
import FutureExpiryMenu from '../../../pages/home/futuresHeader/futureExpiryMenu.tsx'
import OptionsChartStrikeMenu from '../../../pages/home/optionsChartHeader/optionsChartStrikeMenu/optionsChartStrikeMenu.tsx'
import OptionsChartExpiryMenu from '../../../pages/home/optionsChartHeader/optionsChartExpiryMenu/optionsChartExpiryMenu.tsx'

export type SeriesMetadataProps = {
    seriesGroup: SeriesGroup
    primaryChart: boolean
    screenerChart: boolean
    tradeOptions?: TradeOptions
    onSecuritySelected?: (key: string, expressionMode: boolean, securities: string[]) => void
    view: any
}
const SeriesMetadataDisplay: React.FC<SeriesMetadataProps> = ({ seriesGroup, screenerChart, primaryChart, tradeOptions, onSecuritySelected }) => {
    const dispatch = useDispatch()
    const theme = useTheme()
    const chartContext = useContext(ChartContext)
    const indicator = useSelector(selectIndicator(seriesGroup.scriptCode))
    const allIndicators = useSelector(selectIndicators)
    const indicatorScriptCode = sanitizeId(seriesGroup.scriptCode)
    const cachedIndicatorScript = useSelector(selectIndicatorScript(indicatorScriptCode))
    const scriptInputs = useMemo(
        () => parseIndicatorInputsFromScript(cachedIndicatorScript),
        [cachedIndicatorScript],
    )
    const { expressionMode, key, expressionSecurities } = useSelector(selectPrimarySecurity)
    const { id, category, primary, series } = seriesGroup
    const isCandlestickPatternSeriesGroup = isCandlestickPatternIndicator(indicator)
    const isChartPatternSeriesGroup = isChartPatternIndicator(indicator)
    const isPatternIndicatorSeriesGroup = isPatternIndicator(indicator)
    const showLogo = useMemo(() => {
        return primaryChart || screenerChart
    }, [primaryChart, screenerChart])
    const { viewKey } = chartContext!
    const [open, setOpen] = useState(false)
    const [removeDependentsDialogOpen, setRemoveDependentsDialogOpen] = useState(false)
    const [removeDependents, setRemoveDependents] = useState<IndicatorSourceDependentDisplay[]>([])
    const [activePlotTitles, setActivePlotTitles] = useState<ReadonlySet<string> | undefined>()
    const [plotTitlesPending, setPlotTitlesPending] = useState(false)
    const [producerPlotRevision, setProducerPlotRevision] = useState(0)
    const [openMore, setOpenMore] = useState(false)
    const [openMoreTooltip, setOpenMoreTooltip] = useState(false)
    const panes = useSelector(selectChartPanes(viewKey))
    const panesConfig = useSelector(selectChartPanesConfig(viewKey))
    const seriesConfig = useSelector(selectChartSeries(viewKey))
    const { chart } = chartContext!
    const seriesId = seriesGroup.series[0]
    const [visible, setVisible] = useState(() => {
        const sg = chart?.seriesGroupRef?.[id]
        if (sg) return sg.getGroupVisibility()
        if (!seriesId) return true
        const output = seriesGroup.outputValues?.[seriesId]
        const series = seriesConfig[seriesId]
        return (output?.visibility ?? series?.visibility ?? true) !== false
    })
    const chartSeriesGroups = useSelector(selectChartSeriesGroups(viewKey))
    const [active, setActive] = useState<boolean>(false)
    const [openDrawingSettings, setOpenDrawingSettings] = useState<boolean>(false)
    const [drawingIds, setDrawingIds] = useState<string[]>([])
    const chartSecurities = useMemo(() => chartContext?.chart?.securities, [chartContext?.chart?.securities])
    const chartExpressionMode = useMemo(() => chartContext?.chart?.expression, [chartContext?.chart?.expression])
    const [showCumulative, setShowCumulative] = useState<boolean>(false)
    const chartSecurityKey = chartExpressionMode || !chartSecurities?.length ? '' : getSecurityKey(chartSecurities?.[0]?.exchange, chartSecurities?.[0]?.code)
    const [security, setSecurity] = useState<Security | undefined>()
    const priceMultiplier = security?.priceMultiplier ?? chartSecurities?.[0]?.priceMultiplier ?? 100
    const [tradedSecurity, setTradedSecurity] = useState<Security | undefined>()

    useEffect(() => {
        if (!chartSecurityKey) return
        getSecurityAsync(chartSecurityKey).then((sec) => {
            setSecurity(sec)
            if (!sec) return
            if (sec.derived) {
                const refKey =
                    store.getState().securityStore.referenceSecurityMap[getSecurityKey(sec.exchange, sec.code)]
                if (refKey) {
                    getSecurityAsync(refKey).then(setTradedSecurity)
                }
            } else {
                setTradedSecurity(sec)
            }
        })
    }, [chartSecurityKey])

    const chartSettingsStatusLine = useSelector(selectChartConfigSettings(viewKey))
    const chartTradingSettings = useSelector(selectChartTradingSettings(viewKey))
    const tradingAccounts = useSelector(selectTradingAccounts)
    const selectedTradingAccount = useSelector(selectPrimaryTradingAccount)
    const [showSurveillanceDialog, setShowSurveillanceDialog] = useState(false)
    const [embedStrategyLegsExpanded, setEmbedStrategyLegsExpanded] = useState(false)
    const isEmbedded = useSelector(selectIsEmbedded)
    const userAlerts = useSelector(selectUserAlerts)
    // Check if we're in scalper embedded mode by checking tradeOptions
    const isScalperEmbedded = useMemo(() => {
        return isEmbedded && !!tradeOptions
    }, [isEmbedded, tradeOptions])
    const showOptionStrikesMenu = useMemo(() => {
        return !!(!chartExpressionMode && chartSecurities?.length && primary && chartSecurities[0].categoryCode === SecurityCategory_CategoryCode.OPTIONS)
    }, [chartExpressionMode, chartSecurities, primary])

    const placeOrderRequestRef = useRef<PlaceOrModifyOrdersRequest>(null)

    const surveillanceInfo = useMemo(() => {
        const code = Number(tradedSecurity?.surveillanceIndicator)
        return {
            symbol: tradedSecurity?.code ?? '',
            warningTitle: 'Regulatory Caution',
            cautions: code ? [String(code)] : []
        }
    }, [tradedSecurity])

    const securityValid = useMemo(() => {
        if (security?.expiry?.expiry) {
            return isDatePassed(security.expiry.expiry) ? false : true
        }
        return true
    }, [security])

    useEffect(() => {
        if (seriesGroup.name === 'OI Profile') {
            setShowCumulative((seriesGroup.outputValues[seriesGroup.series[0]]?.drawingSettings?.settings?.cumulative ?? false) as boolean)
        }
    }, [seriesGroup])

    useEffect(() => {
        setEmbedStrategyLegsExpanded(false)
    }, [key])

    const disableInput = useMemo(() => {
        return indicator?.name === 'Open Interest Outlook' && ![SecurityCategory_CategoryCode.STOCKS, SecurityCategory_CategoryCode.INDEX].includes(security?.categoryCode as any)
    }, [security?.categoryCode, indicator?.name])

    const showOptionExpiriesMenu = useMemo(() => {
        return !!(!chartExpressionMode && chartSecurities?.length && primary && chartSecurities[0].categoryCode === SecurityCategory_CategoryCode.OPTIONS)
    }, [chartExpressionMode, chartSecurities, primary])

    const showFutureExpiriesMenu = useMemo(() => {
        return !!(!chartExpressionMode && chartSecurities?.length && primary && chartSecurities[0].categoryCode === SecurityCategory_CategoryCode.FUTURES)
    }, [chartExpressionMode, chartSecurities, primary])

    const [underlyingName, setUnderlyingName] = useState('')
    useEffect(() => {
        if (chartExpressionMode || !chartSecurities?.length) {
            setUnderlyingName('')
            return
        }
        if (chartSecurities[0].hasFo) {
            setUnderlyingName(chartSecurities[0].symbol ?? '')
            return
        }
        const underlyingKey = chartSecurities[0].underlyingSecurityCode
            ? getSecurityKey(chartSecurities[0].exchange, chartSecurities[0].underlyingSecurityCode)
            : null
        if (!underlyingKey) {
            setUnderlyingName('')
            return
        }
        getSecurityAsync(underlyingKey).then((sec) => setUnderlyingName(sec?.symbol ?? ''))
    }, [chartExpressionMode, chartSecurities])

    const derived = useMemo(() => !chartExpressionMode && chartSecurities?.length && chartSecurities[0].derived, [chartExpressionMode, chartSecurities])
    const [alertsAnchorEl, setAlertsAnchorEl] = useState<HTMLElement | null>(null)

    const primarySecurityKey = useMemo(() => {
        const sec = chartSecurities?.[0]
        if (!sec || chartExpressionMode) return ''
        return getSecurityKey(sec.exchange, sec.code)
    }, [chartSecurities, chartExpressionMode])

    const normalizeIndicatorInputs = useCallback((values: Record<string, string>) => {
        const normalized: Record<string, string> = {}
        Object.entries(values || {}).forEach(([k, v]) => {
            normalized[sanitizeId(k)] = String(v ?? '')
        })
        return normalized
    }, [])

    const indicatorAlertsForSeriesGroup = useMemo(() => {
        if (!primarySecurityKey) return []
        if (primary || category === SeriesGroupCategory.SERIES_COMPARABLE_SECURITY || category === SeriesGroupCategory.SERIES_DRAWING) return []
        if (category === SeriesGroupCategory.SERIES_VOLUME) {
            return userAlerts.filter((alert) => {
                if (alert.status !== UserAlert_Status.ACTIVE) return false
                if (alert.type === UserAlert_Type.SCREENER) return false
                if (!isUserAlertVolumeLevelThreshold(alert)) return false
                const topLevelKey = alert.security?.code ? getSecurityKey(alert.security.exchange, alert.security.code) : ''
                const [underlying] = getUserAlertUnderlyingSecurity(alert)
                const underlyingKey = underlying?.[0]?.code ? getSecurityKey(underlying[0].exchange, underlying[0].code) : ''
                return topLevelKey === primarySecurityKey || underlyingKey === primarySecurityKey
            })
        }
        const outputValues = new Set<string>()
        seriesGroup.series.forEach((sid) => {
            const conf = seriesConfig[sid]
            if (!conf) return
            if (conf.code) outputValues.add(String(conf.code))
            if (conf.title) outputValues.add(String(conf.title))
            if (conf.name) outputValues.add(String(conf.name))
        })
        if (!outputValues.size) return []
        const normalizedInputs = normalizeIndicatorInputs(seriesGroup.inputValues)
        const indicatorSeriesGroupLookup = buildIndicatorSeriesGroupLookup(chartSeriesGroups)
        return userAlerts.filter((alert) => {
            if (alert.status !== UserAlert_Status.ACTIVE) return false
            if (alert.type === UserAlert_Type.SCREENER) return false
            const topLevelKey = alert.security?.code ? getSecurityKey(alert.security.exchange, alert.security.code) : ''
            const [underlying] = getUserAlertUnderlyingSecurity(alert)
            const underlyingKey = underlying?.[0]?.code ? getSecurityKey(underlying[0].exchange, underlying[0].code) : ''
            if (topLevelKey !== primarySecurityKey && underlyingKey !== primarySecurityKey) return false
            const sac = getPrimarySecurityAlertCondition(alert.condition)
            const lhs = sac?.lhsExpression
            if (!lhs || lhs.type !== AlertExpression_Type.TECHNICAL_INDICATOR || !lhs.indicator) return false
            if (lhs.indicator.indicatorCode !== seriesGroup.scriptCode) return false
            if (!outputValues.has(String(lhs.indicator.output))) return false
            const alertInputs = flattenAlertIndicatorParamsForChart(
                lhs.indicator as AlertIndicator,
                undefined,
                allIndicators,
                indicatorSeriesGroupLookup
            )
            return indicatorInputValuesSemanticallyEqual(
                alertInputs,
                normalizedInputs,
                indicatorSeriesGroupLookup,
                allIndicators
            )
        })
    }, [primarySecurityKey, primary, category, seriesGroup, seriesConfig, userAlerts, normalizeIndicatorInputs, chartSeriesGroups, allIndicators])

    const comparableAlertsForSeriesGroup = useMemo(() => {
        if (!primarySecurityKey) return []
        if (category !== SeriesGroupCategory.SERIES_COMPARABLE_SECURITY) return []
        const compareSecurityKey = seriesGroup.securityList?.[0]
        if (!compareSecurityKey) return []
        return userAlerts.filter((alert) => {
            if (alert.status !== UserAlert_Status.ACTIVE) return false
            if (alert.type === UserAlert_Type.SCREENER) return false
            const topLevelKey = alert.security?.code ? getSecurityKey(alert.security.exchange, alert.security.code) : ''
            if (topLevelKey !== primarySecurityKey) return false
            const sac = getPrimarySecurityAlertCondition(alert.condition)
            const lhs = sac?.lhsExpression
            if (!lhs || lhs.type !== AlertExpression_Type.SIMPLE_SECURITY || !lhs.simpleSecurity?.security) return false
            const lhsKey = getSecurityKey(lhs.simpleSecurity.security.exchange, lhs.simpleSecurity.security.code)
            return lhsKey === compareSecurityKey
        })
    }, [primarySecurityKey, category, seriesGroup.securityList, userAlerts])

    const groupedAlerts = useMemo(() => {
        return category === SeriesGroupCategory.SERIES_COMPARABLE_SECURITY ? comparableAlertsForSeriesGroup : indicatorAlertsForSeriesGroup
    }, [category, comparableAlertsForSeriesGroup, indicatorAlertsForSeriesGroup])

    const alertBadge = useMemo(() => {
        if (!groupedAlerts.length || screenerChart || isScalperEmbedded) return null
        return (
            <NonInteractiveTooltip placement="top" title={`Alerts · ${groupedAlerts.length}`} arrow>
                <Box
                    onClick={(e) => {
                        e.stopPropagation()
                        setAlertsAnchorEl(e.currentTarget as HTMLElement)
                    }}
                    sx={{
                        height: '24px',
                        display: 'inline-flex',
                        alignItems: 'center',
                        cursor: 'pointer',
                        color: '#25B39E'
                    }}
                >
                    <AlarmIcon style={{ fontSize: '24px' }} />
                </Box>
            </NonInteractiveTooltip>
        )
    }, [groupedAlerts, screenerChart, isScalperEmbedded])

    const derivedDetails: any = useMemo(() => {
        if (!primary || !derived) return undefined
        const primarySnap =
            getChartSnapshotSeriesGroup(viewKey, 'primary') ??
            getEffectiveChartSnapshotSeriesGroup(viewKey, 'primary')
        if (!primarySnap) return undefined
        const [, data] = Object.entries(primarySnap)[0] ?? []
        const lastCandle = data?.[(data?.length ?? 0) - 1] as { referencedSecurityCode?: string } | undefined
        const referenceSecurityCode = lastCandle?.referencedSecurityCode ?? ''
        if (showOptionExpiriesMenu) {
            return parseDetailsFromOptionSecurityCode(referenceSecurityCode, chartSecurities?.[0]?.priceMultiplier ?? 100)
        }
        return parseDetailsFromFutureSecurityCode(referenceSecurityCode)
    }, [primary, derived, showOptionExpiriesMenu, showFutureExpiriesMenu, chartSecurities, viewKey])

    const metadataChartSecurityKey =
        seriesGroup.primary && chartSecurityKey ? chartSecurityKey : seriesGroup.securityList?.[0]

    const securityDisplayName = useMemo(() => {
        if (chartExpressionMode) return seriesGroup.name
        if (category === SeriesGroupCategory.SERIES_COMPARABLE_SECURITY) return seriesGroup.name
        const currentSecurity = chartSecurities[0]
        if (currentSecurity && currentSecurity.hasFo) return seriesGroup.name
        return underlyingName
    }, [chartExpressionMode, category, chartSecurities, seriesGroup.name, underlyingName])

    let name: JSX.Element = primary || category === SeriesGroupCategory.SERIES_COMPARABLE_SECURITY
        ? <span className="series-meta-title-content">{securityDisplayName}</span>
        : <span></span>
    let expressionName: string = primary || category === SeriesGroupCategory.SERIES_COMPARABLE_SECURITY ? securityDisplayName : ''

    if (screenerChart) {
        name = <span className="series-meta-title-content">{seriesGroup.name}</span>
        expressionName = seriesGroup.name
    }

    const onTitleClick = useCallback(() => {
        const isExpression = seriesGroup.securityList.length > 1
        dispatch(
            setSecuritySearchOpen({
                open: true,
                key: isExpression ? expressionName : seriesGroup.securityList[0],
                isExpression,
                onSecuritySelected
            })
        )
    }, [seriesGroup.securityList, expressionName, dispatch, onSecuritySelected])

    const handlePlaceOrder = useCallback(async (request: PlaceOrModifyOrdersRequest) => {
        try {
            const created = Date.now()
            const response = await placeOrModifyOrder(request)
            const order = response.orderResponse.length ? response.orderResponse[0] : null
            if (!order) throw new Error()
            const { successful, commitOrder, brokerOrderId, orderId, errorMessage } = order
            if (!successful) {
                addOrderAlertMessage({
                    ...request.order[0],
                    status: OrderStatus.FAILED,
                    message: errorMessage || 'Unable to place order'
                })
            } else {
                const updateOrder: Order = {
                    ...request.order[0],
                    brokerOrderId,
                    status: OrderStatus.OPEN,
                    filledQuantity: 0,
                    orderId,
                    created
                }
                if (commitOrder) {
                    dispatch(updateOrderHistory({ updates: [updateOrder] }))
                }
                addOrderAlertMessage({
                    ...updateOrder,
                    message: 'Order placed successfully'
                })
            }
        } catch (error: any) {
            addOrderAlertMessage({
                ...request.order[0],
                status: OrderStatus.FAILED,
                message: error.message || 'Unable to place order'
            })
        }
    }, [])
    const handleQuickTrade = useCallback(
        (
            transaction: TransactionType,
            orderType: OrderType = OrderType.NORMAL,
            brokerOrderType: Broker_OrderType = Broker_OrderType.MARKET_ORDER,
            product: Product = Product.INTRADAY,
            price: number | undefined = undefined
        ) => {
            let quantity = tradedSecurity?.lotSize || 1
            let finalOrderType = brokerOrderType
            let finalProduct = product

            if (!tradedSecurity) return

            if (tradeOptions) {
                const { quantityMultiplier, orderType: tradeOrderType, orderProduct } = tradeOptions
                quantity = (tradedSecurity.lotSize ?? 1) * (quantityMultiplier ?? 1)
                finalOrderType = Number(tradeOrderType) ?? brokerOrderType
                finalProduct = orderProduct ?? product
            }

            const intraday = Number(finalProduct) === Product.INTRADAY

            if (!intraday) {
                if (tradedSecurity.categoryCode === SecurityCategory_CategoryCode.FUTURES || tradedSecurity.categoryCode === SecurityCategory_CategoryCode.OPTIONS) {
                    finalProduct = Product.MARGIN
                } else {
                    finalProduct = Product.CNC
                }
            }

            if (tradeOptions?.quickTrade) {
                const request: PlaceOrModifyOrdersRequest = {
                    buyBeforeSell: false,
                    tradingAccountId: selectedTradingAccount.accountId ?? '',
                    order: [
                        {
                            security: {
                                code: tradedSecurity?.code ?? '',
                                exchange: tradedSecurity?.exchange ?? 0
                            },
                            transactionType: transaction,
                            orderType: OrderType.NORMAL,
                            brokerOrderType: finalOrderType,
                            quantity: Number(quantity),
                            product: finalProduct,
                            price: finalOrderType === Broker_OrderType.MARKET_ORDER ? undefined : price !== undefined ? Math.round(price) : undefined,
                            triggerPrice: 0,
                            brokerVariety: Broker_Variety.REGULAR,
                            validity: Validity.DAY,
                            clientOrderId: uuid(),
                            batchId: uuid(),
                            orderDate: Date.now(),
                            isBasket: false,
                            immediateExecutePaperOrder: false
                        }
                    ]
                }

                if (!selectedTradingAccount || !tradingAccounts || tradingAccounts?.length === 0) {
                    dispatch(
                        openConnectAccountDialog({
                            type: 'connect',
                            tradingAccountId: ''
                        })
                    )
                    return
                } else if (selectedTradingAccount.status !== TradingAccountStatus.ACTIVE) {
                    dispatch(
                        openConnectAccountDialog({
                            type: 'reconnect',
                            tradingAccountId: selectedTradingAccount.accountId
                        })
                    )
                    return
                } else if (tradedSecurity.surveillanceIndicator) {
                    placeOrderRequestRef.current = request
                    setShowSurveillanceDialog(true)
                    return
                }

                handlePlaceOrder(request)
            } else {
                dispatch(
                    setQuickOrderData({
                        openDialog: true,
                        mode: 'regular',
                        type: 'trade',
                        isSquareOffOrder: false,
                        order: {
                            transactionType: transaction,
                            security: tradedSecurity,
                            editOrder: undefined,
                            brokerOrderType: finalOrderType,
                            orderType,
                            product: finalProduct,
                            price: finalOrderType === Broker_OrderType.MARKET_ORDER ? undefined : price,
                            quantity,
                            maxQuantity: 0
                        }
                    })
                )
            }
        },
        [dispatch, tradedSecurity, tradeOptions, selectedTradingAccount, tradingAccounts]
    )

    const displayText = useMemo(() => {
        if (!expressionMode) {
            return security?.symbol || 'Security'
        }
        return convertExpressionToDisplayExpression(key, expressionSecurities)
    }, [expressionMode, expressionSecurities, security, key, chartSecurityKey])

    if (showLogo) {
        const security = chartContext?.chart?.securities?.[0]
        if (security) {
            name = (
                <Stack direction={'row'} spacing={1} alignItems={'center'} className="series-meta-title-content">
                    {(chartSettingsStatusLine?.logo ?? true) && showLogo && primary ? (
                        security.logoUrl ? (
                            <Avatar alt={seriesGroup.name} src={`${LOGOS_BASE_URL}${security.logoUrl}.svg`} sx={{ width: 18, height: 18 }} />
                        ) : (
                            <Avatar
                                alt={displayText}
                                sx={{
                                    width: 18,
                                    height: 18,
                                    fontSize: 10,
                                    color: 'white',
                                    bgcolor: getColorForSymbol(security?.symbol || security?.code || '', theme.palette.background.default)
                                }}
                            >
                                {(security?.symbol || security?.code || '')
                                    .split(' ')
                                    .map((word) => word[0]?.toUpperCase())
                                    .slice(0, 2)
                                    .join('')}
                            </Avatar>
                        )
                    ) : null}
                    {screenerChart && onSecuritySelected ? (
                        <ExtraSmallButton
                            variant={"contained"}
                           
                            sx={{
                                height: '24px',
                               display: 'flex',
                                alignItems: 'center',
                                cursor: 'pointer',
                                padding: 1,
                                minWidth: 'auto',
                                textTransform: 'none',
                                backgroundColor: (theme) => theme.palette.background.default,
                                color:(theme) => theme.palette.text.primary,
                                '&:hover': {
                                    backgroundColor: (theme) => theme.palette.background.default,
                                }
                            }}
                            onClick={(e) => {
                                e.stopPropagation()
                                if (chartSecurities?.[0]) {
                                    const secKey = getSecurityKey(chartSecurities[0].exchange, chartSecurities[0].code)
                                    onSecuritySelected(secKey, false, [secKey])
                                }
                            }}
                        >
                           <Typography fontSize={'13px'}> {name} </Typography>
                        </ExtraSmallButton>
                    ) : (
                        <Typography variant={'body2'} sx={{ height: '24px', display: 'flex', alignItems: 'center' }}>
                            {name}
                        </Typography>
                    )}
                </Stack>
            )
        }
    }
    name = category === SeriesGroupCategory.SERIES_DRAWING
        ? (
            <span className="series-meta-indicator-title">
                <span className="series-meta-indicator-name">
                    <span>{indicator?.shortName ?? seriesGroup.name}</span>
                    {showCumulative && <span>Cumulative</span>}
                </span>
            </span>
        )
        : category === SeriesGroupCategory.SERIES_VOLUME
            ? isScalperEmbedded
                ? <span></span>
                : <span className="series-meta-title-content">{indicator?.shortName ?? indicator?.name ?? 'Volume'}</span>
            : name
    const [paneId] = useMemo(() => {
        return panes.filter((pid) => {
            const p = panesConfig[pid]
            return p && p.seriesGroups.indexOf(id) !== -1
        })
    }, [id, panes, panesConfig])

    const onRemoveClick = useCallback(() => {
        if (category === SeriesGroupCategory.SERIES_COMPARABLE_SECURITY) {
            chart?.removeComparableSecurity(id)
            return
        }
        if (!chart) return

        if (
            category === SeriesGroupCategory.SERIES_INDICATOR ||
            category === SeriesGroupCategory.SERIES_VOLUME
        ) {
            const dependents = chart.getIndicatorSourceDependentDisplays(id)
            if (dependents.length > 0) {
                setRemoveDependents(dependents)
                setRemoveDependentsDialogOpen(true)
                return
            }
        }

        if (paneId && panesConfig[paneId].seriesGroups.length === 1) {
            chart.removePane(panes.indexOf(paneId))
        } else {
            chart.removeSeries(id)
        }
    }, [category, id, chart, paneId, panesConfig, panes])

    const onConfirmRemoveWithDependents = useCallback(() => {
        chart?.removeIndicatorSeriesGroupTree(id)
        setRemoveDependentsDialogOpen(false)
        setRemoveDependents([])
    }, [chart, id])

    const onCloseRemoveDependentsDialog = useCallback(() => {
        setRemoveDependentsDialogOpen(false)
        setRemoveDependents([])
    }, [])
    const handleKeyDown = useCallback(
        (e: any) => {
            const key = e.key
            const hasOpenDialog = !!document.querySelector('[role="dialog"]')
            if (!primary && active && !e.target.matches('input, textarea') && !hasOpenDialog && onRemoveClick && ['backspace', 'delete'].indexOf(key.toLowerCase()) !== -1) {
                onRemoveClick()
            }
        },
        [primary, active, onRemoveClick]
    )
    useEffect(() => {
        document.addEventListener('keydown', handleKeyDown)

        return () => {
            document.removeEventListener('keydown', handleKeyDown)
        }
    }, [handleKeyDown])

    const onDrawingDestroyed = useCallback(
        (event: CustomEvent) => {
            const { id: drawingId } = event.detail
            if (chart && id) {
                const dIds = chart.getDrawingIds(id)
                if (dIds.includes(drawingId)) {
                    setDrawingIds(dIds.filter((dId) => dId !== drawingId))
                    setOpenDrawingSettings(false)
                }
            }
        },
        [id, chart]
    )

    useEffect(() => {
        chart?.subscribeEvent(ChartEvents.DRAWING_DESTROYED, onDrawingDestroyed)
        return () => {
            chart?.unsubscribeEvent(ChartEvents.DRAWING_DESTROYED, onDrawingDestroyed)
        }
    }, [chart, onDrawingDestroyed])

    const handleMenuOpen = useCallback(() => {
        setOpenMoreTooltip(false)
        setOpenMore(true)
    }, [])

    const handleMenuClose = useCallback(() => {
        setOpenMoreTooltip(false)
        setOpenMore(false)
    }, [])

    const resolveSeriesGroupInputValue = useCallback((
        inputValues: Record<string, string>,
        input: IndicatorInput,
    ): string | undefined => {
        const raw =
            inputValues[input.code]
            ?? inputValues[sanitizeId(input.code || '')]
            ?? inputValues[input.title]
            ?? inputValues[sanitizeId(input.title || '')]
        if (raw === undefined || raw === '') {
            const fallback = input.defval
            return fallback !== undefined && fallback !== '' ? String(fallback) : undefined
        }
        return String(raw)
    }, [])

    const indicatorInputLabels = useMemo(() => {
        if (!seriesGroup.inputValues || !indicator) return []
        const inputs = isPatternIndicatorSeriesGroup
            ? getPatternConfigurationFields(indicator, scriptInputs)
            : (indicator.inputs ?? [])
        if (!inputs.length) return []
        const firstInputGroup = inputs[0]?.group
        const labels: string[] = []
        inputs.forEach((input) => {
            if (isPatternLabelColorInput(input)) {
                return
            }
            if (!isPatternIndicatorSeriesGroup && input?.group !== firstInputGroup) {
                return
            }
            if (isPatternIndicatorSeriesGroup && input.type === IndicatorInput_InputType.BOOLEAN) {
                return
            }
            if (input.type === IndicatorInput_InputType.BOOLEAN) {
                const value = resolveSeriesGroupInputValue(seriesGroup.inputValues, input)
                if (value && value !== 'false' && !disableInput) {
                    labels.push(input.title)
                }
            } else {
                const value = resolveSeriesGroupInputValue(seriesGroup.inputValues, input)
                if (value) {
                    labels.push(
                        isIndicatorPriceSourceInput(input)
                            ? (chart?.getIndicatorOutputSourceLabel(value) ?? getIndicatorPriceSourceLabel(value))
                            : value
                    )
                }
            }
        })
        return labels
    }, [
        seriesGroup.inputValues,
        indicator,
        isPatternIndicatorSeriesGroup,
        scriptInputs,
        disableInput,
        resolveSeriesGroupInputValue,
        chart,
    ])

    const showIndicatorInputsInTitle = indicatorInputLabels.length > 0
        && (isPatternIndicatorSeriesGroup || !!chartSettingsStatusLine?.indicatorInputs)

    const indicatorTitleNode = useMemo(() => {
        if (
            primary ||
            category === SeriesGroupCategory.SERIES_COMPARABLE_SECURITY ||
            category === SeriesGroupCategory.SERIES_VOLUME
        ) {
            return null
        }
        if (category === SeriesGroupCategory.SERIES_DRAWING && !isPatternIndicatorSeriesGroup) {
            return (
                <span className="series-meta-indicator-title">
                    <span className="series-meta-indicator-name">
                        <span>{indicator?.shortName ?? seriesGroup.name}</span>
                        {showCumulative && <span>Cumulative</span>}
                    </span>
                </span>
            )
        }
        if (showIndicatorInputsInTitle) {
            return (
                <span className="series-meta-indicator-title">
                    <span className="series-meta-indicator-name">
                        <span>{indicator?.shortName ?? seriesGroup.name}</span>
                        {showCumulative && <span>Cumulative</span>}
                    </span>
                    <span className="series-meta-indicator-inputs">
                        {indicatorInputLabels.map((label, index) => (
                            <Typography component={'span'} fontSize={'13px'} sx={{ lineHeight: '24px' }} key={index} color={'text.disabled'} className="series-meta-input-labels">
                                {label}
                            </Typography>
                        ))}
                    </span>
                </span>
            )
        }
        return (
            <span className="series-meta-indicator-title">
                <span className="series-meta-indicator-name">
                    <span>{indicator?.shortName ?? seriesGroup.name}</span>
                    {showCumulative && <span>Cumulative</span>}
                </span>
            </span>
        )
    }, [
        primary,
        category,
        seriesGroup.name,
        indicator,
        showCumulative,
        isPatternIndicatorSeriesGroup,
        showIndicatorInputsInTitle,
        indicatorInputLabels,
    ])

    useEffect(() => {
        if (!isPatternIndicatorSeriesGroup) return
        if ((indicator?.inputs?.length ?? 0) > 0) return
        if (cachedIndicatorScript) return
        getIndicatorScript({ code: seriesGroup.scriptCode }).catch(() => undefined)
    }, [
        isPatternIndicatorSeriesGroup,
        indicator?.inputs?.length,
        cachedIndicatorScript,
        seriesGroup.scriptCode,
    ])

    const handlePopUpClose = () => {
        setOpen(false)
    }

    const isIndicatorSeriesGroup =
        category === SeriesGroupCategory.SERIES_INDICATOR || category === SeriesGroupCategory.SERIES_VOLUME

    const tracksActivePlotTitles = isIndicatorSeriesGroup || isPatternIndicatorSeriesGroup

    const isCompareSeriesGroup = category === SeriesGroupCategory.SERIES_COMPARABLE_SECURITY

    const compareSourceFields = useMemo<IndicatorInput[]>(() => {
        if (!isCompareSeriesGroup) return []
        return [
            {
                title: 'Source',
                type: IndicatorInput_InputType.SOURCE,
                defval: 'close',
                minval: '',
                maxval: '',
                options: [],
                group: '',
                tooltip: '',
                display: true,
                code: COMPARABLE_SECURITY_SOURCE_INPUT_CODE
            }
        ]
    }, [isCompareSeriesGroup])

    const onCompareInputsUpdate = useCallback((inputValues: Record<string, any>) => {
        chart?.updateSeriesGroupInputs(id, inputValues, true)
    }, [chart, id])

    const compareLineStyle = useMemo<ChartLineStyle>(() => {
        if (!isCompareSeriesGroup) return 'solid'
        const seriesId = seriesGroup.series[0]
        const options = seriesConfig[seriesId]?.options as { lineStyle?: LineStyle } | undefined
        return lineStyleEnumToChartLineStyle(options?.lineStyle)
    }, [isCompareSeriesGroup, seriesConfig, seriesGroup.series])

    const onCompareLineStyleChange = useCallback((style: ChartLineStyle) => {
        const seriesId = seriesGroup.series[0]
        if (!seriesId) return
        chart?.updateCompareSeriesLineStyle(id, seriesId, style)
    }, [chart, id, seriesGroup.series])

    const chartIndicatorSeriesGroupKey = useMemo(() => {
        return Object.keys(chartSeriesGroups)
            .filter((seriesGroupId) => {
                const groupCategory = chartSeriesGroups[seriesGroupId]?.category
                return groupCategory === SeriesGroupCategory.SERIES_INDICATOR ||
                    groupCategory === SeriesGroupCategory.SERIES_VOLUME
            })
            .sort()
            .join(',')
    }, [chartSeriesGroups])

    const indicatorOutputSources = useMemo((): IndicatorOutputSourceOption[] => {
        if (!chart || !isIndicatorSeriesGroup) return []

        const options = chart.getAvailableIndicatorOutputSources(id)
        indicator?.inputs?.forEach((input) => {
            if (!isIndicatorPriceSourceInput(input)) return
            const currentValue = seriesGroup.inputValues[input.code]
            if (!isIndicatorOutputSource(currentValue)) return
            if (options.some((option) => option.value === currentValue)) return

            const ref = decodeIndicatorOutputSource(currentValue)
            if (!ref) return

            options.push({
                value: currentValue,
                label: chart.getIndicatorOutputSourceLabel(currentValue),
                seriesGroupId: ref.seriesGroupId,
                outputCode: ref.outputCode,
            })
        })

        return options
    }, [
        chart,
        id,
        isIndicatorSeriesGroup,
        indicator?.inputs,
        seriesGroup.inputValues,
        chartIndicatorSeriesGroupKey,
        producerPlotRevision,
    ])

    const getSourceLabel = useCallback((value: string) => {
        return chart?.getIndicatorOutputSourceLabel(value) ?? getIndicatorPriceSourceLabel(value)
    }, [chart])

    const getOiProfileConfigurationSourceLabel = useCallback(
        (value: string) => {
            return OI_PROFILE_DATA_RENDER_TYPE_LABELS[value] ?? getSourceLabel(value)
        },
        [getSourceLabel]
    )

    const syncActivePlotTitles = useCallback(() => {
        setActivePlotTitles(chart?.getIndicatorActivePlotTitles(id))
        setPlotTitlesPending(false)
    }, [chart, id])

    useEffect(() => {
        if (open && tracksActivePlotTitles) {
            setProducerPlotRevision((revision) => revision + 1)
        }
    }, [open, tracksActivePlotTitles])

    useEffect(() => {
        if (!chart || !tracksActivePlotTitles) return
        syncActivePlotTitles()
        const previousOnPlotTitlesUpdated = chart.callbacks.onIndicatorPlotTitlesUpdated
        chart.callbacks.onIndicatorPlotTitlesUpdated = (seriesGroupId: string) => {
            previousOnPlotTitlesUpdated?.(seriesGroupId)
            if (seriesGroupId === id) {
                syncActivePlotTitles()
            } else {
                setProducerPlotRevision((revision) => revision + 1)
            }
        }
        return () => {
            chart.callbacks.onIndicatorPlotTitlesUpdated = previousOnPlotTitlesUpdated
        }
    }, [chart, id, tracksActivePlotTitles, syncActivePlotTitles])

    useEffect(() => {
        if (!chart) return

        const onSeriesMetadataRowActivated = (event: CustomEvent<{ seriesGroupId?: string }>) => {
            if (screenerChart) return
            const activatedId = event.detail?.seriesGroupId
            setActive(activatedId === id)
        }

        chart.subscribeEvent(ChartEvents.SERIES_METADATA_ROW_ACTIVATED, onSeriesMetadataRowActivated)
        return () => {
            chart.unsubscribeEvent(ChartEvents.SERIES_METADATA_ROW_ACTIVATED, onSeriesMetadataRowActivated)
        }
    }, [chart, id, screenerChart])

    const onInputsUpdate = (inputValues: Record<string, any>, refresh: boolean = true) => {
        if (open && refresh) {
            setPlotTitlesPending(true)
        }
        chart.updateSeriesGroupInputs(id, inputValues, refresh)
    }

    const onOutputsUpdate = (outputValues: Record<string, SeriesOutput>) => {
        if (seriesId && outputValues[seriesId]?.visibility !== undefined) {
            setVisible(outputValues[seriesId].visibility !== false)
        }
        chart.updateSeriesGroupOutputs(id, outputValues)
    }

    const onOutputValuesPrecisionUpdate = (precision: number | undefined) => {
        chart.updateSeriesGroupOutputValuesPrecision(id, precision)
    }

    const onDisplaySettingsUpdate = (settings: { showLabelOnPriceScale?: boolean; showValueOnPriceScale?: boolean }) => {
        chart.updateSeriesGroupDisplaySettings(id, settings)
    }

    const onInputsAndOutputsUpdate = (inputValues: Record<string, any>, outputValues: Record<string, SeriesOutput>, refresh: boolean = true) => {
        if (seriesId && outputValues[seriesId]?.visibility !== undefined) {
            setVisible(outputValues[seriesId].visibility !== false)
        }
        chart.updateSeriesGroupOutputsAndInputs(id, outputValues, inputValues, refresh)
    }

    const isOiProfileSeriesGroup = seriesGroup.scriptCode === 'OI_PROFILE' || seriesGroup.name === 'OI Profile'

    const usesSeriesGroupConfigPopup =
        category !== SeriesGroupCategory.SERIES_DRAWING
        || isOiProfileSeriesGroup
        || isCandlestickPatternSeriesGroup
        || isChartPatternSeriesGroup
        || isChartPatternSeriesGroup

    const getChartPatternDefaultInputValues = useCallback(() => {
        return buildPatternDefaultInputValues(indicator, scriptInputs)
    }, [indicator, scriptInputs])

    const getChartPatternDefaultOutputValues = useCallback(() => {
        return buildPatternDefaultOutputValues(
            seriesGroup.series,
            seriesGroup.outputValues,
            indicator,
        )
    }, [seriesGroup, indicator])

    const oiProfilePopupInputValues = useMemo(() => {
        if (!isOiProfileSeriesGroup) {
            return seriesGroup.inputValues
        }
        const seriesId = seriesGroup.series[0]
        const drawingSettings = seriesGroup.outputValues?.[seriesId]?.drawingSettings?.settings ?? {}
        return {
            ...seriesGroup.inputValues,
            dataRenderType: drawingSettings.dataRenderType ?? 'openInterestChange',
            cumulative: drawingSettings.cumulative ?? true
        }
    }, [isOiProfileSeriesGroup, seriesGroup])

    const candlestickPopupInputValues = useMemo(() => {
        if (!isCandlestickPatternSeriesGroup) {
            return seriesGroup.inputValues
        }
        const values = { ...seriesGroup.inputValues }
        if (values[PATTERN_LABEL_COLOR_INPUT_CODE]) {
            return values
        }
        const outputSettings = getPatternDrawingSettingsMap(seriesGroup.outputValues?.[seriesId]?.drawingSettings)
        const bias = getCandlestickPatternBias(indicator, seriesGroup.scriptCode)
        const labelColor = bias === 'bearish'
            ? outputSettings.labelColorBearish
            : outputSettings.labelColorBullish
        if (labelColor) {
            values[PATTERN_LABEL_COLOR_INPUT_CODE] = String(labelColor)
        }
        return values
    }, [isCandlestickPatternSeriesGroup, seriesGroup, seriesId, indicator])

    const oiProfileConfigurationFields = useMemo(() => {
        if (!isOiProfileSeriesGroup) {
            return indicator?.inputs ?? []
        }
        return [...OI_PROFILE_INPUT_FIELDS, ...(indicator?.inputs ?? [])]
    }, [isOiProfileSeriesGroup, indicator?.inputs])

    const seriesGroupConfigurationFields = useMemo(() => {
        if (isOiProfileSeriesGroup) {
            return oiProfileConfigurationFields
        }
        if (isPatternIndicatorSeriesGroup) {
            return ensureCandlestickPatternConfigurationFields(indicator, scriptInputs)
        }
        return indicator?.inputs ?? []
    }, [
        isOiProfileSeriesGroup,
        oiProfileConfigurationFields,
        indicator,
        isPatternIndicatorSeriesGroup,
        scriptInputs,
    ])

    const getOiProfileDefaultInputValues = useCallback(() => {
        const values: Record<string, any> = {}
        oiProfileConfigurationFields.forEach((field) => {
            if (field.type === IndicatorInput_InputType.BOOLEAN) {
                values[field.code] = field.defval === 'true' || field.defval === true
            } else {
                values[field.code] = field.defval ?? ''
            }
        })
        return values
    }, [oiProfileConfigurationFields])

    const getOiProfileDefaultOutputValues = useCallback((): Record<string, SeriesOutput> => {
        const seriesId = seriesGroup.series[0]
        const current = seriesId ? seriesGroup.outputValues[seriesId] : undefined
        if (!seriesId || !current || !chart) {
            return { ...seriesGroup.outputValues }
        }

        const defaults = getIndicatorDrawingSettings('oiProfile', chart.chartApi?.options() ?? {}, { settings: {} })
        const colorKeys = [
            'primaryCallColor',
            'secondaryCallColor',
            'primaryPutColor',
            'secondaryPutColor',
        ] as const
        const colors = colorKeys.map((key) => ({
            title: key,
            colorCode: defaults[key]?.color ?? '',
        }))
        const settings = {
            primaryCallColor: defaults.primaryCallColor.color,
            secondaryCallColor: defaults.secondaryCallColor.color,
            primaryPutColor: defaults.primaryPutColor.color,
            secondaryPutColor: defaults.secondaryPutColor.color,
            textColor: defaults.textColor.color,
            showLabels: defaults.showLabels,
            showCallPutOnSameSide: defaults.showCallPutOnSameSide,
            cumulative: defaults.cumulative,
            dataRenderType: defaults.dataRenderType,
            barThickness: defaults.barThickness,
            barWidthPercent: defaults.barWidthPercent,
            indicator: defaults.indicator,
        }

        return {
            ...seriesGroup.outputValues,
            [seriesId]: {
                ...current,
                colors,
                drawingSettings: { settings },
            },
        }
    }, [chart, seriesGroup])

    const onCandlestickPatternInputsUpdate = useCallback(
        (inputValues: Record<string, any>, refresh: boolean = true) => {
            if (!chart) return
            const seriesId = seriesGroup.series[0]
            const labelColorCode = PATTERN_LABEL_COLOR_INPUT_CODE

            const indicatorInputValues = { ...seriesGroup.inputValues }
            Object.entries(inputValues).forEach(([key, value]) => {
                indicatorInputValues[key] = value?.toString?.() ?? value
            })

            let outputValues = { ...seriesGroup.outputValues }
            const labelColor = inputValues[labelColorCode]
            if (seriesId && labelColor != null && String(labelColor).trim() !== '') {
                const patternBias = getCandlestickPatternBias(indicator, seriesGroup.scriptCode) ?? 'bullish'
                const stylePatch = buildCandlestickLabelColorStylePatch(patternBias, String(labelColor))
                outputValues = {
                    ...outputValues,
                    [seriesId]: {
                        ...seriesGroup.outputValues[seriesId],
                        drawingSettings: buildPatternDrawingSettingsUpdate(
                            seriesGroup.outputValues[seriesId]?.drawingSettings,
                            stylePatch,
                        ),
                    },
                }
                chart.getDrawingIds(id).forEach((drawingId) => {
                    chart.updateDrawingSettings(drawingId, stylePatch)
                })
            }

            if (open && refresh) {
                setPlotTitlesPending(true)
            }
            chart.updateSeriesGroupOutputsAndInputs(id, outputValues, indicatorInputValues, refresh)
        },
        [chart, id, indicator, open, seriesGroup],
    )

    const onOiProfileInputsUpdate = useCallback(
        (inputValues: Record<string, any>, refresh: boolean = true) => {
            if (!chart) return
            const seriesId = seriesGroup.series[0]
            const drawingUpdates: Record<string, string | boolean> = {}
            if ('dataRenderType' in inputValues) {
                drawingUpdates.dataRenderType = inputValues.dataRenderType?.toString()
            }
            if ('cumulative' in inputValues) {
                drawingUpdates.cumulative = inputValues.cumulative === true || inputValues.cumulative === 'true'
            }

            const indicatorInputValues = { ...seriesGroup.inputValues }
            Object.entries(inputValues).forEach(([key, value]) => {
                if (OI_PROFILE_INPUT_SETTING_KEYS.has(key)) return
                indicatorInputValues[key] = value?.toString?.() ?? value
            })

            let outputValues = { ...seriesGroup.outputValues }
            if (seriesId && Object.keys(drawingUpdates).length > 0) {
                outputValues = {
                    ...outputValues,
                    [seriesId]: {
                        ...seriesGroup.outputValues[seriesId],
                        drawingSettings: {
                            settings: {
                                ...(seriesGroup.outputValues[seriesId]?.drawingSettings?.settings ?? {}),
                                ...drawingUpdates
                            }
                        }
                    }
                }
                chart.getDrawingIds(id).forEach((drawingId) => {
                    chart.updateDrawingSettings(drawingId, drawingUpdates)
                })
            }

            chart.updateSeriesGroupOutputsAndInputs(id, outputValues, indicatorInputValues, refresh)
        },
        [chart, id, seriesGroup]
    )

    const extraDrawingSettings = useMemo(() => {
        const seriesId = seriesGroup.series[0]
        const drawingSettings = seriesGroup.outputValues?.[seriesId]?.drawingSettings?.settings ?? {}
        const oiProfileInputSettings = isOiProfileSeriesGroup
            ? [
                  {
                      name: 'dataRenderType',
                      label: 'Type of Data',
                      type: SettingType.SELECT,
                      defaultValue: drawingSettings.dataRenderType ?? 'openInterestChange',
                      custom: true,
                      visible: true,
                      options: [
                          { label: 'Open Interest', value: 'openInterest' },
                          { label: 'OI Change', value: 'openInterestChange' }
                      ]
                  },
                  {
                      name: 'cumulative',
                      label: 'Cumulative',
                      type: SettingType.BOOLEAN,
                      defaultValue: drawingSettings.cumulative ?? true,
                      custom: true,
                      visible: true
                  }
              ]
            : []
        const indicatorInputSettings =
            indicator?.inputs?.map((input) => {
                const type = mapIndicatorInputTypeToDrawingSettingType(input.type)
                return {
                    name: input.title,
                    label: input.title,
                    type: isIndicatorSelectInput(input) ? SettingType.SELECT : type,
                    defaultValue: seriesGroup.inputValues[input.title] ?? input.defval,
                    custom: true,
                    visible: true,
                    range: [Number(input.minval), Number(input.maxval ?? 10)],
                    options: isIndicatorSelectInput(input)
                        ? [
                              ...getIndicatorInputSelectOptions(input).map((option) => ({
                                  label: isIndicatorPriceSourceInput(input)
                                      ? getIndicatorPriceSourceLabel(option)
                                      : option,
                                  value: option,
                              })),
                              ...(isIndicatorPriceSourceInput(input)
                                  ? indicatorOutputSources.map((option) => ({
                                        label: option.label,
                                        value: option.value,
                                    }))
                                  : []),
                          ]
                        : []
                }
            }) ?? []
        const settings = [...oiProfileInputSettings, ...indicatorInputSettings]
        if (!settings.length) {
            return []
        }
        return [
            {
                id: 'indicatorInputs',
                displayName: 'Inputs',
                visible: true,
                settings
            }
        ]
    }, [isOiProfileSeriesGroup, indicator, seriesGroup, indicatorOutputSources])

    const onDrawingSettingGroupUpdated = (sgs: DrawingSettingGroup<any, any>[]) => {
        const indicatorInputsSettingGroup = sgs?.find((sg) => sg.id === 'indicatorInputs')
        let inputsChange: boolean = false
        let outputsChange: boolean = false
        let inputValues = { ...seriesGroup.inputValues }
        let outputValues = { ...seriesGroup.outputValues }
        const dIds = chart.getDrawingIds(id)

        //check inputs change
        if (indicatorInputsSettingGroup) {
            const inputs = indicatorInputsSettingGroup.settings.reduce((acc: any, curr) => {
                acc[curr.name] = curr.defaultValue
                return acc
            }, {})

            if (isOiProfileSeriesGroup) {
                const seriesId = seriesGroup.series[0]
                const prevDrawingSettings = seriesGroup.outputValues[seriesId]?.drawingSettings?.settings ?? {}
                const oiProfileInputUpdates: Record<string, string | boolean> = {}
                OI_PROFILE_INPUT_SETTING_KEYS.forEach((key) => {
                    if (!(key in inputs)) return
                    const newVal =
                        key === 'cumulative'
                            ? inputs[key] === true || inputs[key] === 'true'
                            : inputs[key]?.toString()
                    const oldVal = prevDrawingSettings[key]
                    if (String(oldVal) !== String(newVal)) {
                        oiProfileInputUpdates[key] = newVal as string | boolean
                    }
                })
                if (Object.keys(oiProfileInputUpdates).length > 0) {
                    outputsChange = true
                    const newSettings = { ...prevDrawingSettings, ...oiProfileInputUpdates }
                    outputValues = {
                        ...outputValues,
                        [seriesId]: {
                            ...seriesGroup.outputValues[seriesId],
                            drawingSettings: {
                                settings: newSettings
                            }
                        }
                    }
                    dIds.forEach((drawingId) => {
                        chart.updateDrawingSettings(drawingId, oiProfileInputUpdates)
                    })
                }
            }

            const indicatorOnlyInputs = Object.keys(seriesGroup.inputValues).reduce((acc: Record<string, string>, key) => {
                acc[key] = inputs[key]?.toString() ?? seriesGroup.inputValues[key]
                return acc
            }, {})
            Object.keys(seriesGroup.inputValues).forEach((key) => {
                if (inputsChange) {
                    return
                }
                if (indicatorOnlyInputs[key]?.toString() !== seriesGroup.inputValues[key].toString()) {
                    inputsChange = true
                    return
                }
            })
            if (inputsChange) {
                inputValues = indicatorOnlyInputs
            }
        }

        //check outputs change
        dIds.forEach((drawingId) => {
            const availableStyleSettings = chart.drawingManager.getDrawingSettings(drawingId).find((s) => s.id === 'style') as DrawingSettingGroup<any, any>
            const settingsValues = chart.drawingManager.getSettingsValues(drawingId)
            const seriesId = chart.getSeriesIdOfDrawingId(id, drawingId)
            if (!seriesId) {
                return
            }
            const newSeriesDrawingSettings = { ...(seriesGroup.outputValues[seriesId]?.drawingSettings?.settings ?? {}) }

            let changed: boolean = false
            Object.keys(newSeriesDrawingSettings).forEach((key) => {
                const type = availableStyleSettings.settings?.find((s) => s.name === key)?.type ?? undefined
                switch (type) {
                    case SettingType.BOOLEAN: {
                        if (newSeriesDrawingSettings[key].toString() !== settingsValues[key].toString()) {
                            changed = true
                            newSeriesDrawingSettings[key] = settingsValues[key]
                        }
                        break
                    }
                    case SettingType.COLOR: {
                        if (newSeriesDrawingSettings[key].toString() !== settingsValues[key].color.toString()) {
                            newSeriesDrawingSettings[key] = settingsValues[key].color
                            changed = true
                        }
                        break
                    }
                    case SettingType.SELECT: {
                        if (newSeriesDrawingSettings[key].toString() !== settingsValues[key].toString()) {
                            newSeriesDrawingSettings[key] = settingsValues[key]
                            changed = true
                        }
                        break
                    }
                    case SettingType.NUMBER: {
                        if (Number(newSeriesDrawingSettings[key]) !== Number(settingsValues[key])) {
                            changed = true
                            newSeriesDrawingSettings[key] = settingsValues[key]
                        }
                        break
                    }
                }
            })

            if (changed) {
                outputsChange = true
                outputValues = {
                    ...outputValues,
                    [seriesId]: {
                        ...seriesGroup.outputValues[seriesId],
                        drawingSettings: {
                            settings: { ...newSeriesDrawingSettings }
                        }
                    }
                }
            }
        })

        if (inputsChange && outputsChange) {
            onInputsAndOutputsUpdate(inputValues, outputValues)
        } else if (inputsChange) {
            onInputsUpdate(inputValues)
        } else if (outputsChange) {
            onOutputsUpdate(outputValues)
        }
    }

    const visibilityButtonClick = useCallback(
        (e: any) => {
            const sg = chart.seriesGroupRef[id]
            if (!sg) return
            const nextVisible = !visible
            const output = sg.config.outputValues?.[seriesId] ?? seriesGroup.outputValues?.[seriesId]

            if (output?.drawingType && seriesId) {
                const outputValues = {
                    ...(sg.config.outputValues ?? {}),
                    [seriesId]: {
                        ...output,
                        visibility: nextVisible,
                    },
                }
                chart.updateSeriesGroupOutputs(id, outputValues)
            } else {
                chart.toggleVisibility(id, nextVisible)
            }
            setVisible(nextVisible)
            e.stopPropagation()
        },
        [chart, id, visible, seriesId, seriesGroup.outputValues]
    )

    const onClickAway = useCallback((event: MouseEvent | TouchEvent) => {
        if (!active) return
        const target = event.target
        if (target instanceof Node && chart?.container?.contains(target)) return
        setActive(false)
    }, [active, chart])

    const hiddenTitleSectionClassName = !visible ? 'series-meta-title-section--hidden' : ''

    const hideShowButtons = !screenerChart ? (
        <NonInteractiveTooltip placement={'top'} title={visible ? 'Hide' : 'Show'} arrow>
            <UltraSmallButton
                sx={{ maxHeight: '24px', borderRadius: '2px' }}
                color={'inherit'}
                onClick={visibilityButtonClick}
                data-event={`SeriesMetadata Series ${visible ? 'Hidden' : 'Shown'}`}
                data-event-series-name={seriesGroup.name}
            >
                {visible ? (
                    <Show key="series-visible" sx={{ fontSize: '1rem' }} />
                ) : (
                    <Hide key="series-hidden" sx={{ fontSize: '1rem' }} />
                )}
            </UltraSmallButton>
        </NonInteractiveTooltip>
    ) : null

    const settingsButton = useMemo(() => {
        if (screenerChart) return null

        const hasInputsOrOutputs = indicator?.inputs?.length || indicator?.outputs?.length
        const shouldShowButton = category === SeriesGroupCategory.SERIES_DRAWING || hasInputsOrOutputs || isCompareSeriesGroup

        if (!shouldShowButton) return null

        const handleClick = () => {
            if (isCompareSeriesGroup) {
                setOpen(true)
                return
            }
            if (isEmbedded) {
                if (category === SeriesGroupCategory.SERIES_DRAWING) {
                    if (chart && id) {
                        const drawingIds = chart.getDrawingIds(id)
                        const settingsGroups = [...chart.getDrawingSettings(drawingIds[0]), ...extraDrawingSettings]
                        callNative('onDrawingSettingsClicked', id, drawingIds[0], JSON.stringify(settingsGroups))
                        return
                    }
                } else {
                    callNative('onSeriesMetaDataSettingsClicked', seriesGroup.id)
                    return
                }
            } else {
                if (category === SeriesGroupCategory.SERIES_DRAWING) {
                    if (usesSeriesGroupConfigPopup) {
                        setOpen(true)
                    } else if (chart && id) {
                        setOpenDrawingSettings(true)
                        setDrawingIds(chart.getDrawingIds(id))
                    }
                } else {
                    setOpen(true)
                }
            }
        }

        return (
            <NonInteractiveTooltip placement="top" title="Settings" arrow>
                <UltraSmallButton
                    sx={{ maxHeight: '24px', borderRadius: '2px' }}
                    color="inherit"
                    size="small"
                    onClick={handleClick}
                    data-event="SeriesMetadata Settings Clicked"
                    data-event-series-name={seriesGroup.name}
                >
                    <SettingsOutlinedIcon sx={{ fontSize: '18px' }} color="inherit" />
                </UltraSmallButton>
            </NonInteractiveTooltip>
        )
    }, [seriesGroup, screenerChart, category, indicator, chart, id, isEmbedded, usesSeriesGroupConfigPopup])

    const closeButton = useMemo(() => {
        return (
            !screenerChart &&
            !primary && (
                <NonInteractiveTooltip placement={'top'} title="Remove" arrow>
                    <UltraSmallButton
                        sx={{ maxHeight: '24px', borderRadius: '2px' }}
                        color="inherit"
                        size="small"
                        onClick={onRemoveClick}
                        data-event={'SeriesMetadata Series Removed'}
                        data-event-series-name={seriesGroup.name}
                    >
                        <BinIcon sx={{ fontSize: '1rem' }} color={'inherit'} />
                    </UltraSmallButton>
                </NonInteractiveTooltip>
            )
        )
    }, [seriesGroup, onRemoveClick, screenerChart, primary])

    const seriesGroupMenu = useMemo(() => {
        return (
            !screenerChart && (
                <NonInteractiveTooltip placement={'top'} title="More" arrow open={openMoreTooltip} onOpen={() => !openMore && setOpenMoreTooltip(true)} onClose={() => setOpenMoreTooltip(false)}>
                    <div style={{ height: '100%' }}>
                        <SeriesGroupMenu seriesGroup={seriesGroup} disabled={!securityValid} onClosed={handleMenuClose} onOpened={handleMenuOpen} />
                    </div>
                </NonInteractiveTooltip>
            )
        )
    }, [seriesGroup, handleMenuClose, handleMenuOpen, openMoreTooltip, openMore, screenerChart, securityValid])

    const strategyEmbeddedLegLabels = useMemo(() => {
        if (!isEmbeddedStrategyChartContext() || !primary || screenerChart) {
            return null
        }
        if (chartContext?.chart?.mode !== 'EXPRESSION') {
            return null
        }
        const rawExpr = chartContext.chart.expression?.trim() ?? ''
        const expr = normalizeExpressionKeyFromUrl(rawExpr).trim()
        if (!expr) {
            return null
        }
        const securities = chartContext.chart.securities ?? []
        const legs = getEmbeddedStrategyExpressionLegDisplayStrings(expr, securities)
        return legs.length > 1 ? legs : null
    }, [primary, screenerChart, chartContext?.chart?.mode, chartContext?.chart?.expression, chartContext?.chart?.securities])

    const nameContainer = useMemo(() => {
        if (strategyEmbeddedLegLabels) {
            const [firstLeg, ...otherLegs] = strategyEmbeddedLegLabels
            return (
                <Box
                    className="series-meta-embedded-multi-leg series-meta-title-content"
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'flex-start',
                        alignSelf: 'stretch',
                        minWidth: 0,
                        width: '100%',
                        maxWidth: '100%',
                    }}
                    onClick={(e) => e.stopPropagation()}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'row',
                            alignItems: 'center',
                            flexWrap: 'nowrap',
                            gap: 0.25,
                            minWidth: 0,
                            maxWidth: '100%',
                        }}
                    >
                        <NonInteractiveTooltip placement={'top'} title="Change symbol" arrow>
                            <UltraSmallButton
                                size={'small'}
                                variant="text"
                                color="inherit"
                                sx={{
                                    padding: 0,
                                    margin: 0,
                                    display: 'inline-flex',
                                    minWidth: 0,
                                    maxHeight: 'none',
                                    borderRadius: '2px',
                                    flex: '1 1 auto',
                                    textAlign: 'left',
                                    justifyContent: 'flex-start',
                                }}
                                onClick={() => {
                                    const isExpression = seriesGroup.securityList.length > 1
                                    dispatch(
                                        setSecuritySearchOpen({
                                            open: true,
                                            key: isExpression ? (expressionName as string) : seriesGroup.securityList[0],
                                            isExpression: isExpression,
                                            onSecuritySelected: onSecuritySelected,
                                        }),
                                    )
                                }}
                            >
                                <Typography
                                    variant="body2"
                                    sx={{
                                        lineHeight: 1.25,
                                        textAlign: 'left',
                                        wordBreak: 'break-word',
                                        overflowWrap: 'anywhere',
                                    }}
                                >
                                    {firstLeg}
                                </Typography>
                            </UltraSmallButton>
                        </NonInteractiveTooltip>
                        <IconButton
                            size="small"
                            sx={{ flexShrink: 0, padding: '2px' }}
                            onClick={(e) => {
                                e.stopPropagation()
                                setEmbedStrategyLegsExpanded((v) => !v)
                            }}
                            aria-label={embedStrategyLegsExpanded ? 'Hide legs' : 'Show all legs'}
                        >
                            {embedStrategyLegsExpanded ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
                        </IconButton>
                    </Box>
                    <Collapse in={embedStrategyLegsExpanded} timeout="auto" unmountOnExit sx={{ width: '100%' }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, pt: 0.5, pl: 0.5, width: '100%' }}>
                            {otherLegs.map((leg, i) => (
                                <Typography
                                    key={`${leg}-${i}`}
                                    variant="body2"
                                    sx={{
                                        lineHeight: 1.25,
                                        color: 'text.secondary',
                                        wordBreak: 'break-word',
                                        overflowWrap: 'anywhere',
                                    }}
                                >
                                    {leg}
                                </Typography>
                            ))}
                        </Box>
                    </Collapse>
                </Box>
            )
        }
        return (
            <NonInteractiveTooltip placement={'top'} title="Change symbol" arrow>
                <UltraSmallButton
                    size={'small'}
                    variant="text"
                    color="inherit"
                    sx={{ padding: 0, margin: 0, display: 'inline-block', maxHeight: '24px', borderRadius: '2px' }}
                    onClick={() => {
                        const isExpression = seriesGroup.securityList.length > 1
                        dispatch(
                            setSecuritySearchOpen({
                                open: true,
                                key: isExpression ? (expressionName as string) : seriesGroup.securityList[0],
                                isExpression: isExpression,
                                onSecuritySelected: onSecuritySelected
                            })
                        )
                    }}
                >
                    {name}
                </UltraSmallButton>
            </NonInteractiveTooltip>
        )
    }, [
        strategyEmbeddedLegLabels,
        embedStrategyLegsExpanded,
        name,
        expressionName,
        seriesGroup.securityList,
        dispatch,
        onSecuritySelected,
    ])

    const seriesGroupError = chartContext?.seriesGroupErrors?.[seriesGroup.id]
    const errorContainer = seriesGroupError ? (
        <NonInteractiveTooltip title={seriesGroupError} arrow placement="top">
            <span>
                <UltraSmallButton sx={{ maxHeight: '24px', color: 'red', cursor: 'default' }} color="inherit" disabled disableRipple disableFocusRipple>
                    <ErrorIcon sx={{ fontSize: '1rem', color: '#cc2f3c88' }} />
                </UltraSmallButton>
            </span>
        </NonInteractiveTooltip>
    ) : null

    const tradeLotSize = useMemo(() => tradedSecurity?.lotSize ?? security?.lotSize ?? 1, [tradedSecurity?.lotSize, security?.lotSize])

    const [quantity, setQuantity] = useState(tradeLotSize * (tradeOptions?.quantityMultiplier ?? 1))

    useEffect(() => {
        setQuantity(tradeLotSize * (tradeOptions?.quantityMultiplier ?? 1))
    }, [tradeLotSize, tradeOptions?.quantityMultiplier])

    const setQuantityInput = useCallback((value: number)=>{
        const newTradeOptions = {...tradeOptions}
        newTradeOptions.quantityMultiplier = (value??1)/(tradeLotSize ?? 1)
        setQuantity(value)
        dispatch(setTradeOptions(newTradeOptions))
    }, [tradeLotSize, tradeOptions])
    
    const quantityInput = useMemo(() => {
        if (!tradeOptions || !tradeOptions.quickTrade) return null

        return (
            <Box sx={{ height: 24, display: 'flex', alignItems: 'center' }}>
                <NumberInputField
                    value={quantity}
                    onValueChange={setQuantityInput}
                    minValue={tradeLotSize}
                    stepSize={tradeLotSize}
                    sx={{ background: (theme) => theme.palette.background.paper }}
                />
            </Box>
        )
    }, [tradeOptions, quantity, tradeLotSize, setQuantityInput])

    const onSurveillanceConfirm = useCallback(() => {
        placeOrderRequestRef.current && handlePlaceOrder(placeOrderRequestRef.current)
        placeOrderRequestRef.current = null
        setShowSurveillanceDialog(false)
    }, [])

    const onSurveillanceCancel = useCallback(() => {
        placeOrderRequestRef.current = null
        setShowSurveillanceDialog(false)
    }, [])

    if (!chartContext?.chart) return null

    return (
        <>
            <ClickAwayListener onClickAway={onClickAway}>
                <Box
                    sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        '--series-meta-hidden-title-color': (t) => t.palette.text.disabled,
                    }}
                    className="series-meta-container"
                >
                    <span
                        className="series-meta-name"
                        style={{ fontSize: '13px' }}
                        onClick={() => {
                            const nextActive = !active && !screenerChart
                            setActive(nextActive)
                            if (nextActive) {
                                chart?.publishEvent(ChartEvents.SERIES_METADATA_ROW_ACTIVATED, { seriesGroupId: id })
                            }
                        }}
                    >
                        {!screenerChart ? (
                            <span className="series-meta-content-row">
                                <span className={`series-meta-title-section ${hiddenTitleSectionClassName}`.trim()}>
                                    <Box
                                        className={`series-meta-buttons-wrapper ${openMore || active ? 'series-meta-buttons-wrapper-clicked-state' : ''}`}
                                        sx={{
                                            display: screenerChart ? 'none' : 'flex',
                                            backgroundColor: (theme) => theme.palette.background.paper,
                                            borderColor: (theme) => (active ? theme.palette.primary.main : theme.palette.divider)
                                        }}
                                    >
                                        {hideShowButtons}
                                        {settingsButton}
                                        {closeButton}
                                        {seriesGroupMenu}
                                    </Box>
                                    <div className="series-meta-title-content" style={{ display: 'flex', gap: 0.25 }}>
                                    {primary ? (
                                        strategyEmbeddedLegLabels ? (
                                            nameContainer
                                        ) : (
                                            <SeriesMetadataPrimaryToolbar
                                                viewKey={viewKey}
                                                nameContainer={showLogo ? nameContainer : undefined}
                                                title={showLogo ? undefined : securityDisplayName}
                                                onTitleClick={showLogo ? undefined : onTitleClick}
                                                security={security}
                                                expressionMode={expressionMode}
                                                isScalperEmbedded={isScalperEmbedded}
                                                chartSettingsStatusLine={chartSettingsStatusLine}
                                                showOptionExpiriesMenu={showOptionExpiriesMenu}
                                                showOptionStrikesMenu={showOptionStrikesMenu}
                                                showFutureExpiriesMenu={showFutureExpiriesMenu}
                                                derivedDetails={derivedDetails}
                                                chartSecurityKey={metadataChartSecurityKey}
                                            />
                                        )
                                    ) : (
                                        !isScalperEmbedded && (
                                            <Box className="series-meta-title-content" sx={{ display: 'inline-flex', alignItems: 'center' }}>
                                                {(chartSettingsStatusLine?.indicatorTitle ?? true) && (indicatorTitleNode ?? name)}
                                            </Box>
                                        )
                                    )}
                                </div>
                                </span>
                                {alertBadge}
                                {errorContainer}
                                {category !== SeriesGroupCategory.SERIES_DRAWING ? (
                                    <SeriesMetadataLiveValues
                                        viewKey={viewKey}
                                        seriesGroup={seriesGroup}
                                        screenerChart={screenerChart}
                                        expressionMode={expressionMode}
                                        derived={derived}
                                        showOptionExpiriesMenu={showOptionExpiriesMenu}
                                        isScalperEmbedded={isScalperEmbedded}
                                        chartSecurityKey={chartSecurityKey}
                                        priceMultiplier={priceMultiplier}
                                        activePlotTitles={tracksActivePlotTitles ? activePlotTitles : undefined}
                                    />
                                ) : null}
                            </span>
                        ) : (
                            <span className="series-meta-content-row">
                                <span className={`series-meta-title-section ${hiddenTitleSectionClassName}`.trim()}>
                                    <Box
                                        className={`series-meta-buttons-wrapper ${openMore || active ? 'series-meta-buttons-wrapper-clicked-state' : ''}`}
                                        sx={{
                                            display: screenerChart ? 'none' : 'flex',
                                            backgroundColor: (theme) => theme.palette.background.paper,
                                            borderColor: (theme) => (active ? theme.palette.primary.main : theme.palette.divider)
                                        }}
                                    >
                                        {hideShowButtons}
                                        {settingsButton}
                                        {closeButton}
                                        {seriesGroupMenu}
                                    </Box>
                                    <div className="series-meta-title-content">
                                    {primary ? (
                                        <Box sx={{ display: 'flex', alignItems: 'center', flexDirection: 'row', gap: 0.5 }}>
                                            {name}
                                            {showOptionExpiriesMenu && <OptionsChartExpiryMenu defaultName={derivedDetails?.expiryName} chartSecurityKey={metadataChartSecurityKey} />}
                                            {showOptionStrikesMenu && <OptionsChartStrikeMenu chartSecurityKey={metadataChartSecurityKey} />}
                                            {showFutureExpiriesMenu && <FutureExpiryMenu defaultName={derivedDetails?.expiryName} chartSecurityKey={metadataChartSecurityKey} />}
                                        </Box>
                                    ) : (
                                        !isScalperEmbedded && name
                                    )}
                                </div>
                                </span>
                                {alertBadge}
                                {errorContainer}
                                {category !== SeriesGroupCategory.SERIES_DRAWING ? (
                                    <SeriesMetadataLiveValues
                                        viewKey={viewKey}
                                        seriesGroup={seriesGroup}
                                        screenerChart={screenerChart}
                                        expressionMode={expressionMode}
                                        derived={derived}
                                        showOptionExpiriesMenu={showOptionExpiriesMenu}
                                        isScalperEmbedded={isScalperEmbedded}
                                        chartSecurityKey={chartSecurityKey}
                                        priceMultiplier={priceMultiplier}
                                        activePlotTitles={tracksActivePlotTitles ? activePlotTitles : undefined}
                                    />
                                ) : null}
                            </span>
                        )}
                    </span>
                    <span onClick={(e) => e.stopPropagation()}>
                        {!isEmbedded && open && indicator && !isCompareSeriesGroup &&
                            usesSeriesGroupConfigPopup && (
                            <SeriesGroupConfigurationPopup
                                fields={seriesGroupConfigurationFields}
                                id={seriesGroup.id}
                                inputValues={
                                    isOiProfileSeriesGroup
                                        ? oiProfilePopupInputValues
                                        : isCandlestickPatternSeriesGroup
                                            ? candlestickPopupInputValues
                                            : seriesGroup.inputValues
                                }
                                title={indicator.name}
                                indicatorCode={seriesGroup.scriptCode}
                                outputValues={seriesGroup.outputValues}
                                seriesOrder={seriesGroup.series}
                                activePlotTitles={
                                    isCandlestickPatternSeriesGroup || isChartPatternSeriesGroup
                                        ? undefined
                                        : activePlotTitles
                                }
                                plotTitlesPending={plotTitlesPending}
                                outputValuesPrecision={seriesGroup.outputValuesPrecision}
                                showLabelOnPriceScale={seriesGroup.showLabelOnPriceScale}
                                showValueOnPriceScale={seriesGroup.showValueOnPriceScale}
                                onInputsUpdate={
                                    isOiProfileSeriesGroup
                                        ? onOiProfileInputsUpdate
                                        : isCandlestickPatternSeriesGroup
                                            ? onCandlestickPatternInputsUpdate
                                            : onInputsUpdate
                                }
                                onOutputsUpdate={onOutputsUpdate}
                                onOutputValuesPrecisionUpdate={onOutputValuesPrecisionUpdate}
                                onDisplaySettingsUpdate={onDisplaySettingsUpdate}
                                viewKey={viewKey}
                                onClose={handlePopUpClose}
                                onSubmit={handlePopUpClose}
                                security={security}
                                indicatorOutputSources={indicatorOutputSources}
                                getSourceLabel={isOiProfileSeriesGroup ? getOiProfileConfigurationSourceLabel : getSourceLabel}
                                disableAreaFill={category === SeriesGroupCategory.SERIES_VOLUME}
                                hideOutputValuesSettings={isOiProfileSeriesGroup}
                                getDefaultInputValues={
                                    isOiProfileSeriesGroup
                                        ? getOiProfileDefaultInputValues
                                        : (isChartPatternSeriesGroup || isCandlestickPatternSeriesGroup)
                                            ? getChartPatternDefaultInputValues
                                            : undefined
                                }
                                getDefaultOutputValues={
                                    isOiProfileSeriesGroup
                                        ? getOiProfileDefaultOutputValues
                                        : isChartPatternSeriesGroup
                                            ? getChartPatternDefaultOutputValues
                                            : undefined
                                }
                            />
                        )}
                        {open && isCompareSeriesGroup && (
                            <SeriesGroupConfigurationPopup
                                fields={compareSourceFields}
                                id={seriesGroup.id}
                                inputValues={seriesGroup.inputValues}
                                title={seriesGroup.name}
                                outputValues={seriesGroup.outputValues}
                                seriesOrder={seriesGroup.series}
                                outputValuesPrecision={seriesGroup.outputValuesPrecision}
                                showLabelOnPriceScale={seriesGroup.showLabelOnPriceScale}
                                showValueOnPriceScale={seriesGroup.showValueOnPriceScale}
                                onInputsUpdate={onCompareInputsUpdate}
                                onOutputsUpdate={onOutputsUpdate}
                                onOutputValuesPrecisionUpdate={onOutputValuesPrecisionUpdate}
                                onDisplaySettingsUpdate={onDisplaySettingsUpdate}
                                viewKey={viewKey}
                                onClose={handlePopUpClose}
                                onSubmit={handlePopUpClose}
                                security={security}
                                getSourceLabel={getIndicatorPriceSourceLabel}
                                showLineStyle
                                lineStyle={compareLineStyle}
                                onLineStyleChange={onCompareLineStyleChange}
                            />
                        )}
                        {!isEmbedded && !isOiProfileSeriesGroup && openDrawingSettings && drawingIds.length && (
                            <ChartDrawingSettingsModal
                                id={drawingIds[0]}
                                extraSettingsGroups={extraDrawingSettings}
                                onClose={() => setOpenDrawingSettings(false)}
                                onDrawingSettingGroupsUpdated={onDrawingSettingGroupUpdated}
                            />
                        )}
                    </span>
                    <Popover
                        open={Boolean(alertsAnchorEl)}
                        anchorEl={alertsAnchorEl}
                        onClose={() => setAlertsAnchorEl(null)}
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                        PaperProps={{
                            sx: {
                                mt: 0.75,
                                p: '8px',
                                minWidth: 380,
                                maxWidth: 540,
                                borderRadius: '4px',
                                backgroundColor: '#1f2024',
                                boxShadow: '0px 12px 30px rgba(0, 0, 0, 0.35)',
                            }
                        }}
                    >
                        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
                            <Box
                                sx={{
                                    width: 24,
                                    height: 24,
                                    borderRadius: '50%',
                                    backgroundColor: 'rgba(37, 179, 158, 0.16)',
                                    color: '#25B39E',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                }}
                            >
                                <AlarmIcon style={{ fontSize: '24px' }} />
                            </Box>
                            <Typography sx={{ fontWeight: 700, color: '#25B39E', fontSize: '14px' }}>
                                Alerts · {groupedAlerts.length}
                            </Typography>
                        </Stack>
                        <Stack spacing={1.5}>
                            {groupedAlerts.map((alert) => (
                                <Typography key={alert.id} sx={{ fontSize: '13px', color: '#e7e7e8', lineHeight: 1.35 }}>
                                    {alert.message || alert.name || 'Alert'}
                                </Typography>
                            ))}
                        </Stack>
                    </Popover>
                </Box>
            </ClickAwayListener>
            {removeDependentsDialogOpen && (
                <RemoveIndicatorDependentsDialog
                    open={removeDependentsDialogOpen}
                    dependents={removeDependents}
                    onClose={onCloseRemoveDependentsDialog}
                    onConfirm={onConfirmRemoveWithDependents}
                />
            )}
            {showSurveillanceDialog && <SurveillanceWarningDialog open={showSurveillanceDialog} info={surveillanceInfo} onClose={onSurveillanceCancel} onConfirm={onSurveillanceConfirm} />}
            {primary &&
                !screenerChart &&
                !chartExpressionMode &&
                !isScalperEmbedded &&
                !isEmbeddedStrategyChartContext() &&
                (chartTradingSettings?.showBuySellButtons ?? true) &&
                chartSecurities?.[0]?.categoryCode !== SecurityCategory_CategoryCode.INDEX &&
                (tradedSecurity && tradedSecurity?.expiry?.expiry ? !isDatePassed(tradedSecurity.expiry.expiry) : true) ? (
                <SeriesMetadataBidAskControls
                    viewKey={viewKey}
                    priceMultiplier={priceMultiplier}
                    isEmbedded={isEmbedded}
                    tradeOptions={tradeOptions}
                    quantityInput={quantityInput}
                    onQuickTrade={handleQuickTrade}
                />
            ) : null}
        </>
    )
}
export default memo(SeriesMetadataDisplay)
